import React, { FC, Fragment, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { SayThankYouForm } from '@/views/SayThankYouForm/SayThankYouForm'

interface SendThankYouModelProps {
  onSave: () => void
  onCancel: () => void
  projectSlug: string
  show: boolean
}

export const SayThankYouModal: FC<SendThankYouModelProps> = ({ show, onSave, onCancel, projectSlug }) => {
  const { t } = useTranslate('common')
  const { controller } = useAngelPlayerContext()

  useEffect(() => {
    if (controller) {
      if (show) {
        controller.pause()
      } else {
        controller.play()
      }
    }
  }, [controller, show])

  return show ? (
    <Transition appear show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => undefined}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="bg-opacity/60 fixed inset-0 bg-black" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="mt-36 flex justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                  <Translate t={t} i18nKey="sayThankYouModal">
                    Say Thank You
                  </Translate>
                  <Button
                    data-testid="share-nft-close-button"
                    variant="plain"
                    className="float-right flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-sm text-gray-700"
                    onClick={onCancel}
                  >
                    <CloseIcon className="h-4 w-4" />
                  </Button>
                </Dialog.Title>
                <div className="my-4">
                  <SayThankYouForm projectSlug={projectSlug} onSave={onSave} onCancel={onCancel} />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  ) : null
}
